import * as React from 'react';

import { withClock, WithClock } from '../../../../../common/ui/WithClock';
import Editor from '../../../../core/Editor';
import { sanitizeReplicaId } from '../../../../core/EditorUtils';

import { Theme, Grid, Typography, Accordion, AccordionSummary, AccordionDetails, FormControl, InputLabel, Select, MenuItem,IconButton, ListItemIcon, ListItemText, Link, Box, SvgIcon, Tooltip, TextField, Divider, Button, SelectChangeEvent } from '@mui/material';
import { scrollToElement } from '../../utils/ScrollTo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LensIcon from '@mui/icons-material/Lens';
import CloseIcon from '@mui/icons-material/Close';
import CameraAlt from '@mui/icons-material/CameraAlt';
import IEditorSelectionChangeListener from '../../../../core/selection/IEditorSelectionChangeListener';
import EditorSelection from '../../../../core/selection/EditorSelection';
import IEditorModelChangeListener from '../../../../core/listeners/IEditorModelChangeListener';
import Color from 'src/api/model/Color';
import {ObjectFit, WideoColorDef, Class} from '../../../../../common/core/model/WideoDef';
import { grey } from '@mui/material/colors';
import { hexToRGB } from '../../utils/colors/CommonColorPicker';
import { 
  hexToInt, 
  intToHex 
} from 'src/common/ColorUtils';
import { ICONS } from 'src/editor/core/EditorConstants';
import EditorDef from 'src/editor/core/model/EditorDef';
import { WithTranslation, withTranslation } from 'react-i18next';
import WideoObject from 'src/common/core/WideoObject';
import { formatMsToMMSS } from '../audio/AudioAttributes';
import ScriptComponent from 'src/common/core/components/ScriptComponent';
import { v4 as uuid } from 'uuid';

const styles = {
    root: {
        boxShadow: 'none',
        marginBottom: '10px',
    },
    actionPanel: {
        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.12)',
        minHeight: '52px',
        paddingRight: '12px'
    },
    expanded: {
        margin: '0',
    },
    content: {
        margin: '0',
    },
    formControl: {
        minWidth: 175,
        marginBottom: 15,
    },
    button: {
        margin: '0 15px 0 0',
        width: '10px',
        height: '10px',
        boxShadow: 'none',
        color: 'transparent',
        backgroundColor: (theme: Theme) => theme.palette.common.white,
        padding: '0',
        '&:hover': {
        backgroundColor: (theme: Theme) => theme.palette.common.white,
        },
    }, 
    sizeSmallIconButton: {
        borderRadius: '50%',
        width: '0.75em',
        height: '0.75em',
        margin: 0,   
    },
    labelIcon: {
        backgroundColor: 'transparent',
        borderRadius: '50%',
        transition: 'box-shadow 150ms ease-in-out, border 150ms ease-in-out',
    },
    displayListItemIcon: {
      float: 'left' as React.CSSProperties['float'],
      display: 'block',
      minWidth: '22px',
      marginLeft: '5px'
    },
    closeIconButton: {
        margin: '-2.5px 0',
    },
    closeIcon: {
        fontSize: '16px',
    },
    select: {
      padding: '10px 0 0 0',
      marginTop: '5px'      
    },
    label: {
      margin: '10px 0px 10px',
      color: grey['900']
    },
    divider: {
      margin: '30px 0 10px',
      width: '100%'
    },  
    disabled: {
      opacity: '1!important'
    },
    labelItem: {
      color: grey['900'],
      margin: '10px 0 0',
    },                 
};


const tooltip = 
<React.Fragment>
    <b>{"Contain"}</b> - Fit entire replacing object inside the replaced objects area (maintaining aspect ratio)<br/>
    <b>{"Cover"}</b> - The replacing object will cover the entire area of the replaced object (maintaining aspect ratio)<br/>
    <b>{"Fill"}</b> - The replacing object will cover the entire area of the replaced object (without maintaining aspect ratio)<br/>
    <b>{"None"}</b> - The replacing object will use its natural size<br/>
    <b>{"Scale Down"}</b> - Like contain but the object will never be scaled up
</React.Fragment>;

interface Props {
    editor: Editor;
    player: Editor;
}

interface State {
    objectName: string;
    objectFit: ObjectFit | '';
    thumbnail: number | null;
    objectScript: string;
    replicaId: string;
    backgroundUrl: string;
    backgroundSrcUrl: string;
    properties: string;
}

type PropsWithStyle = Props & WithTranslation & WithClock;

class AutomationPanel extends React.Component<PropsWithStyle, State> implements IEditorSelectionChangeListener, IEditorModelChangeListener {

    constructor(props: PropsWithStyle) {
        super(props);
        this.state = this.initState();        
    }

    private initState = (): State => {
        const props: PropsWithStyle = this.props;

        if (props.editor.getSelection().hasObjectSelected()) {
            const selectedObject = this.props.editor.getSelection().getSelectedObject();
            const scriptComponent = this.getScriptComponent(selectedObject);
            
            return {
                objectName: selectedObject.getReplaceableName() || '',
                objectFit: selectedObject.getReplaceObjectFit(), 
                thumbnail: this.props.editor.getThumbnail(),
                objectScript: scriptComponent ? scriptComponent.getScript() : '',
                replicaId: scriptComponent ? sanitizeReplicaId(scriptComponent.getReplicaId() || '') : '',
                backgroundUrl: scriptComponent ? scriptComponent.getBackgroundUrl() : '',
                backgroundSrcUrl: scriptComponent ? scriptComponent.getbackgroundSrcUrl() : '',
                properties: scriptComponent ? scriptComponent.getProperties() : ''
            }
        } else {
            return {
                objectName: '',
                objectFit: '',
                thumbnail: this.props.editor.getThumbnail(),
                objectScript: '',
                replicaId: '',
                backgroundUrl: '',
                backgroundSrcUrl: '',
                properties: ''
            }
        }
    }
    
    onSelectionChange(selection: EditorSelection): void {
      this.setState(() => {
        return this.initState();
      });
    }

    onModelChange(wideoObjects: WideoObject[], undoable: boolean, action: string): void {
      // Only update state if the selected object is in the list of changed objects
      const selectedObject = this.props.editor.getSelection().getSelectedObject();

      const hasSelectedObjectChanged =
        selectedObject &&
        wideoObjects?.some(obj => obj.getId() === selectedObject.getId());

      if (hasSelectedObjectChanged) {
        this.setState(() => {
          return this.initState();
        });
      }
    }

    componentDidMount() {
        this.props.editor.addSelectionChangeListener(this);
        this.props.editor.addModelChangeListener(this);
    }
    
    componentWillUnmount(): void {
        this.props.editor.removeSelectionChangeListener(this);
        this.props.editor.removeModelChangeListener(this);
    }

    handleTemplateColorChange = (index: number) => (event: SelectChangeEvent<number>) => {
      const { value } = event.target;

      const wideoColors = this.props.editor.getParameterColors();
      let newDropdowns = [];

      if (wideoColors && wideoColors.length > index) {
        newDropdowns = [...wideoColors];
        newDropdowns[index] = {
          ...newDropdowns[index],
          color: intToHex(+value)
        };

        this.props.editor.setParameterColor(newDropdowns[index].name, newDropdowns[index].color);
      }
    
      this.forceUpdate();
    };    

    handleRemoveTemplateColor = (index: number) => () => {
      const wideoColors = this.props.editor.getParameterColors();

      if (wideoColors && wideoColors.length > index) {
        const newDropdowns = [...wideoColors];
        const { name } = newDropdowns[index];
        newDropdowns.splice(index, 1);

        this.props.editor.removeParameterColor(name);
        
        this.forceUpdate();
      }
    };    

    objectNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {

      const name = event.target.value;
      const re = /^\w+$/; //Letters, numbers and underscore

      if (re.test(name) || !name) {
        let object: WideoObject = this.props.editor.getSelection().getSelectedObject();
        if (name) {
          if (
              EditorDef.isImageObject(object) ||
              EditorDef.isVideoObject(object)) {

              object = this.props.editor.createPlaceholderFromWideoObject(object);
          }
        } else {
          if (EditorDef.isPlaceholder(object)) {
            this.props.editor.removePlaceholderAndMask(object)
          }
        }
        this.props.editor.setReplaceableName(object, name);
        this.setState({
            objectName: name
        });
      }
    }

    objectFitChange = (event: SelectChangeEvent<number>) => {
      const { value } = event.target;
        this.props.editor.setReplaceObjectFit(this.props.editor.getSelection().getSelectedObject(), value as ObjectFit);
        this.setState({
            objectFit: value as ObjectFit
        });
    };

    handleChangeThumbnail = () => {
      const { currentTime } = this.props;

      this.props.editor.setThumbnail(currentTime);
      this.setState({
        thumbnail: currentTime
      });      
    }

    renderListOfTemplateColors = (dropdownIndex: number): JSX.Element[] => {
      const wideoColors = this.props.editor.getParameterColors();
      let templateColors = this.props.editor.getAllColorsUsedInWideo();

      const wideoColorDefs: Color[] = wideoColors.map((color: WideoColorDef, mapIndex) => {
        return new Color(mapIndex, mapIndex, hexToInt(color.color));
      });
    
      templateColors = [...templateColors, ...wideoColorDefs];

      const items: JSX.Element[] = templateColors.map((color: Color) => {
        const isSelected = wideoColors.some((c) => hexToInt(c.color) === color.rgb);
        const isExcludedInTemplate = wideoColorDefs.some((c) => c.rgb === color.rgb && c.colorId === color.colorId) 

        return this.renderColorItems(color, dropdownIndex, isSelected, isExcludedInTemplate);
      });

      return items;
    }

    isColorDisabled = (index: number, color: Color): boolean => {
      const wideoColors = this.props.editor.getParameterColors();
      
      for (let i = 0; i < wideoColors.length; i++) {
        if (i !== index && color.rgb === hexToInt(wideoColors[i]?.color)) {
          return true;
        }
      }
    
      return false;
    };    

    renderColorItems(color: Color, index: number, isSelected: boolean, isExcludedInTemplate: boolean) {
      const whiteColor = color.rgb === 0xffffff
      const whiteColorActive = isSelected && whiteColor
      const colorBorderState = hexToRGB(color.rgb)
      const boxShadowColorState = whiteColorActive ? `${grey[200]} 0px 0px 0px 3px inset, ${hexToRGB(color.rgb)} 0px 0px 0px 25px inset` : `${colorBorderState} 0px 0px 0px 3px inset, ${hexToRGB(color.rgb)} 0px 0px 0px 25px inset`   
      const isColorDisabled = this.isColorDisabled(index, color)
      return (
          <MenuItem 
            value={color.rgb}
            key={color.rgb} 
            disabled={isColorDisabled}
            style={{
              display: isExcludedInTemplate ? 'none' : 'block'
            }}                 
          >
          <ListItemIcon style={{ ...styles.displayListItemIcon }}>
              <IconButton
                  size="small"
                  style={{
                    ...styles.button,
                    ...styles.labelIcon,
                    ...styles.sizeSmallIconButton
                  }}
              >
                  <LensIcon
                  style={{boxShadow: boxShadowColorState, ...styles.sizeSmallIconButton }}
                  viewBox='0 0 0 0'
                  />
              </IconButton>
              </ListItemIcon>
              <ListItemText 
                style={{ 
                  opacity: isColorDisabled ? 0.5 : 1, 
                  paddingLeft: 2, 
                  color:'#727272', 
                  margin: 0 
                }}>
                  {intToHex(color.rgb)}
              </ListItemText>
        </MenuItem>
        )
    }  

    renderTemplateColorsSection = (templateColor: Color | null, index: number): JSX.Element => {
      const wideoColors = this.props.editor.getParameterColors();
      const selectValue = wideoColors[index]?.color || '';
    
      const colorsJSX: JSX.Element[] = this.renderListOfTemplateColors(index);
      const labels = this.getLabels(colorsJSX.length);

      const isLastDropdown = index === wideoColors.length - 1;
      const isRemoveDisabled = !isLastDropdown;      
    
      return (
        <Grid item key={`${index}${selectValue}`} xs={12}>
          <Grid container alignItems="center" spacing={0}>
            <Grid item xs={8}>
              <FormControl variant='standard' style={styles.formControl}>
                <InputLabel id={`wideo-color-label-${index}`}>
                  <span style={{ textTransform: 'capitalize' }}>{labels[index]} Color</span>
                </InputLabel>
                <Select
                  labelId={`wideo-colors-select-label-id-${index}`}
                  id={`wideo-colors-select-${index}`}
                  label={labels[index]}
                  value={hexToInt(selectValue)}
                  onChange={this.handleTemplateColorChange(index)}
                  fullWidth
                  style={{
                    ...styles.select
                  }}
                >
                  {colorsJSX}
                </Select>
              </FormControl>
            </Grid>
            {!isRemoveDisabled && (
              <Grid item>
                <div style={styles.closeIconButton}>
                  <IconButton
                    onClick={this.handleRemoveTemplateColor(index)}
                    aria-label="Remove Dropdown"
                  >
                    <CloseIcon 
                      sx={styles.closeIcon} 
                    />
                  </IconButton>
                </div>
              </Grid>
            )}
          </Grid>
        </Grid>
      );
    }

    renderThumbnailSection = (): JSX.Element => {
      const { t } = this.props;

      return (
          <Grid item xs={12}>
          <Grid container>
                <>
                  <Divider variant="middle" sx={styles.divider} />
                  <Grid item xs={12}>
                      <Typography 
                        gutterBottom 
                        variant="body1" 
                        sx={styles.label}
                        style={{ marginBottom: 10 }}>
                        Thumbnail
                      </Typography>                               
                  </Grid>
                  <Grid item xs={9}>
                    <Button 
                      variant="contained"
                      startIcon={<CameraAlt />} 
                      onClick={this.handleChangeThumbnail}
                      >
                      {t("rightbar.objects.automation.thumbnail.button-text")}
                    </Button>
                    <Typography
                        variant="caption"
                        gutterBottom
                        paragraph={true}
                        sx={styles.labelItem}>
                         {t("rightbar.objects.automation.thumbnail.label")} 
                         <Box component="span" color="primary.main">
                            {this.state.thumbnail ? formatMsToMMSS(this.state.thumbnail) : t("rightbar.objects.automation.thumbnail.no-set")}
                          </Box>
                    </Typography>                                           
                  </Grid>
                </> 
          </Grid>        
        </Grid>
      )
    }       
      

    renderTextPlacheholderSection = (): JSX.Element => {
      const object = this.props.editor.getSelection().getSelectedObject();

      return (
          <Grid item xs={12}>
          <Grid container>
              {(EditorDef.isTextObject(object) || 
                EditorDef.isPlaceholder(object) || 
                EditorDef.isImageObject(object) ||
                EditorDef.isMaskedObject(object) ||
                EditorDef.isVideoObject(object) || 
                EditorDef.isAudioObject(object)) &&  
                <>
                  <Grid item xs={12}>
                      <Typography 
                        gutterBottom 
                        variant="body1" 
                        style={{ marginBottom: 10, ...styles.label }}>
                        {EditorDef.isTextObject(object) ? "Text" : "Placeholder"}
                      </Typography>              
                      <Tooltip title="Parameter name without spaces. Separate words with underscore (_)">
                          <TextField variant='standard' inputProps={{ "data-cy": "maticVariableName" }} value={this.state.objectName} onChange={this.objectNameChange} id="object-name-text-field" label="Parameter name" />
                      </Tooltip>
                  </Grid>
                  
                  {/* Script field right after parameter name */}
                  {this.shouldShowScriptField(object) && (
                    <>
                      <Grid item xs={12} style={{ marginTop: 15 }}>
                        <TextField
                          variant="standard"
                          id="object-script-field"
                          label={"Script"}
                          multiline
                          rows={5}
                          fullWidth
                          value={this.state.objectScript}
                          onChange={this.objectScriptChange}
                          sx={{
                            "& .MuiInput-root": {
                              height: "auto !important"
                            },
                            "& .MuiInputBase-input": {
                              height: "auto !important",
                              minHeight: "unset !important",
                              maxHeight: "none !important"
                            }
                          }}
                        />
                      </Grid>
                      
                      {/* ReplicaId field below script */}
                      <Grid item xs={12} style={{ marginTop: 15 }}>
                        <TextField
                          variant="standard"
                          id="object-replicaid-field"
                          label={"ReplicaId"}
                          fullWidth
                          value={this.state.replicaId}
                          onChange={this.replicaIdChange}
                        />
                      </Grid>
                      
                      {this.props.editor.isSuperUser() && !EditorDef.isAudioObject(object) && (
                        <>
                          {/* BackgroundUrl field */}
                          <Grid item xs={12} style={{ marginTop: 15 }}>
                            <TextField
                              variant="standard"
                              id="object-background-url-field"
                              label={"Background URL"}
                              fullWidth
                              value={this.state.backgroundUrl}
                              onChange={this.backgroundUrlChange}
                            />
                          </Grid>
                          
                          {/* BackgroundSourceUrl field */}
                          <Grid item xs={12} style={{ marginTop: 15 }}>
                            <TextField
                              variant="standard"
                              id="object-background-source-url-field"
                              label={"Background Source URL"}
                              fullWidth
                              value={this.state.backgroundSrcUrl}
                              onChange={this.backgroundSourceUrlChange}
                            />
                          </Grid>
                          
                          {/* Properties field (JSON) */}
                          <Grid item xs={12} style={{ marginTop: 15 }}>
                            <TextField
                              variant="standard"
                              id="object-properties-field"
                              label={"Properties (JSON)"}
                              multiline
                              rows={4}
                              fullWidth
                              value={this.state.properties}
                              onChange={this.propertiesChange}
                              helperText="JSON format."
                              sx={{
                                "& .MuiInput-root": {
                                  height: "auto !important"
                                },
                                "& .MuiInputBase-input": {
                                  height: "auto !important",
                                  minHeight: "unset !important",
                                  maxHeight: "none !important"
                                }
                              }}
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                  
                  <Divider variant="middle" style={styles.divider} />
                </> 
              }
              {EditorDef.isPlaceholder(object) && 
                <> 
                  <Grid item xs={12}>
                      <Tooltip title={tooltip} placement="left">
                          <FormControl variant='standard' style={styles.formControl}>
                              <InputLabel id="object-fit-label">Object Fit</InputLabel>
                              <Select
                                  labelId="object-fit-select-label-id"
                                  id="object-fit-select"
                                  value={this.state.objectFit as any}
                                  onChange={this.objectFitChange}
                                >
                                  <MenuItem value={ObjectFit.Contain}>Contain</MenuItem>
                                  <MenuItem value={ObjectFit.Cover}>Cover</MenuItem>
                                  <MenuItem value={ObjectFit.Fill}>Fill</MenuItem>
                                  <MenuItem value={ObjectFit.None}>None</MenuItem>
                                  <MenuItem value={ObjectFit.ScaleDown}>Scale Down</MenuItem>
                              </Select>
                          </FormControl>
                      </Tooltip>
                  </Grid>
                  <Divider variant="middle" sx={styles.divider} />
                </> 
              }
          </Grid>        
        </Grid>
      )
    }   

    private getScriptComponent = (object: WideoObject): ScriptComponent | null => {
        const components = object.getComponentsByClass(Class.ScriptComponent);
        if (components && components.length > 0) {
            return components[0] as ScriptComponent;
        }
        return null;
    }

    objectScriptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const script = event.target.value;
        const object = this.props.editor.getSelection().getSelectedObject();
        
        let scriptComponent = this.getScriptComponent(object);
        
        if (!scriptComponent && script.trim() !== '') {
            // Create and add the script component if it doesn't exist
            const componentDef = {
                id: uuid(),
                class: Class.ScriptComponent,
                script: script
            };
            
            scriptComponent = new ScriptComponent(
                this.props.editor.getWideoContext(),
                object,
                componentDef
            );
            
            object.addComponent(scriptComponent);
        } else if (scriptComponent && script.trim() !== '') {
            // Update existing script component
            scriptComponent.setScript(script);
        } else if (scriptComponent && script.trim() === '') { 
            // Remove the script component if the script is empty and the component exists
            object.removeComponent(scriptComponent);
        } else {
            // Do nothing
        }
        
        this.setState({
            objectScript: script
        });
        
        this.props.editor.fireModelChangeEvent([object], true, 'scriptChange');
    }

    replicaIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const replicaId = sanitizeReplicaId(event.target.value);
        const object = this.props.editor.getSelection().getSelectedObject();
        
        let scriptComponent = this.getScriptComponent(object);
        
        if (!scriptComponent) {
            // Create and add the script component if it doesn't exist
            const componentDef = {
                id: uuid(),
                class: Class.ScriptComponent,
                script: '',
                replicaId: replicaId
            };
            
            scriptComponent = new ScriptComponent(
                this.props.editor.getWideoContext(),
                object,
                componentDef
            );
            
            object.addComponent(scriptComponent);
        } else {
            // Update existing script component
            scriptComponent.setReplicaId(replicaId);
        }
        
        this.setState({
            replicaId: replicaId
        });
        
        this.props.editor.fireModelChangeEvent([object], true, 'replicaIdChange');
    }

    backgroundUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const backgroundUrl = event.target.value;
        const object = this.props.editor.getSelection().getSelectedObject();
        
        let scriptComponent = this.getScriptComponent(object);
        
        if (!scriptComponent) {
            // Create and add the script component if it doesn't exist
            const componentDef = {
                id: uuid(),
                class: Class.ScriptComponent,
                script: '',
                backgroundUrl: backgroundUrl
            };
            
            scriptComponent = new ScriptComponent(
                this.props.editor.getWideoContext(),
                object,
                componentDef
            );
            
            object.addComponent(scriptComponent);
        } else {
            // Update existing script component
            scriptComponent.setBackgroundUrl(backgroundUrl);
        }
        
        this.setState({
            backgroundUrl: backgroundUrl
        });
        
        this.props.editor.fireModelChangeEvent([object], true, 'backgroundUrlChange');
    }

    backgroundSourceUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const backgroundSrcUrl = event.target.value;
        const object = this.props.editor.getSelection().getSelectedObject();
        
        let scriptComponent = this.getScriptComponent(object);
        
        if (!scriptComponent) {
            // Create and add the script component if it doesn't exist
            const componentDef = {
                id: uuid(),
                class: Class.ScriptComponent,
                script: '',
                backgroundSrcUrl: backgroundSrcUrl
            };
            
            scriptComponent = new ScriptComponent(
                this.props.editor.getWideoContext(),
                object,
                componentDef
            );
            
            object.addComponent(scriptComponent);
        } else {
            // Update existing script component
            scriptComponent.setbackgroundSrcUrl(backgroundSrcUrl);
        }
        
        this.setState({
            backgroundSrcUrl: backgroundSrcUrl
        });
        
        this.props.editor.fireModelChangeEvent([object], true, 'backgroundSourceUrlChange');
    }

    propertiesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const properties = event.target.value;
        const object = this.props.editor.getSelection().getSelectedObject();
        
        if (properties.trim() && properties.trim() !== '') {
            try {
                JSON.parse(properties);
            } catch (e) {
                return;
            }
        }
        
        let scriptComponent = this.getScriptComponent(object);
        
        if (!scriptComponent) {
            // Create and add the script component if it doesn't exist
            const componentDef = {
                id: uuid(),
                class: Class.ScriptComponent,
                script: '',
                properties: properties
            };
            
            scriptComponent = new ScriptComponent(
                this.props.editor.getWideoContext(),
                object,
                componentDef
            );
            
            object.addComponent(scriptComponent);
        } else {
            // Update existing script component
            scriptComponent.setProperties(properties);
        }
        
        this.setState({
            properties: properties
        });
        
        this.props.editor.fireModelChangeEvent([object], true, 'propertiesChange');
    }

    shouldShowScriptField = (object: WideoObject): boolean => {

        if (EditorDef.isAudioObject(object)) {
            return true;
        }
        
        if (EditorDef.isPlaceholder(object)) {
            // For MaskedObject, check if the other object is a VideoObject
            const otherObject = (object as any).getOtherObject();
            if (otherObject && EditorDef.isVideoObject(otherObject)) {
                return true;
            }
        }
        return false;
    }



    getLabels = (numItems: number) => {
      const labels = [];
      const ordinals = ["primary", "secondary", "tertiary"];
    
      for (let i = 0; i < numItems; i++) {
        if (i < 3) {
          labels.push(ordinals[i]);
        } else {
          const ordinal = this.getOrdinal(i + 1);
          if (ordinal === "3rd") {
            labels.push("tertiary");
          } else {
            labels.push(ordinal);
          }
        }
      }
    
      return labels;
    };

    getOrdinal = (n: number) => {
      const ordinals = ["th", "st", "nd", "rd"];
      const module = n % 100;
      const ordinalIndex = (module - 20) % 10;

      return n + (ordinals[ordinalIndex] || ordinals[module] || "th");
    }

    renderDropdowns = (): JSX.Element[] => {
      const wideoColors = this.props.editor.getParameterColors();
      const dropdowns: JSX.Element[] = [];
    
      wideoColors.forEach((wideoColor: WideoColorDef, index: number) => {
        const color = this.props.editor.getAllColorsUsedInWideo().find((c) => hexToInt(c.rgb.toString()) === hexToInt(wideoColor.color));
        dropdowns.push(this.renderTemplateColorsSection(color, index));
      });
    
      return dropdowns;
    };


    addDropdown = (event: React.SyntheticEvent) => {
      event.preventDefault();
      const wideoColors = this.props.editor.getParameterColors();
      const templateColors = this.props.editor.getAllColorsUsedInWideo();
      const newDropdowns = [...wideoColors];

      const usedColors = wideoColors.map((color) => color.color);
      const newColor = templateColors.find((color) => !usedColors.includes(intToHex(color.rgb)));
      
      if (newColor) {
        const newLabel = this.getLabels(newDropdowns.length + 1)[newDropdowns.length]
        const newDropdown: WideoColorDef = {
          name: newLabel,
          color: intToHex(newColor.rgb)
        };
    
        newDropdowns.push(newDropdown);
    
        this.props.editor.addParameterColor(newDropdown.name, newDropdown.color);  
        this.forceUpdate();     
      }
    };

    renderAddDropdownButton = () => {
      const { t } = this.props;

      const wideoColors = this.props.editor.getParameterColors();
      const templateColors = this.props.editor.getAllColorsUsedInWideo();
      let buttonText = t("rightbar.objects.automation.button-text");

      if (wideoColors.length > 0 && wideoColors.length < templateColors.length) {
        buttonText = t("rightbar.objects.automation.long-button-text");
      } else if (wideoColors.length === templateColors.length) {
        return null;
      }
    
      return (
          <Box mt={5}>
              <Link 
                  component="button" 
                  variant="subtitle2" 
                  onClick={this.addDropdown} 
                  underline='always'
                  aria-label="Add Dropdown">
                  {buttonText}
              </Link>
          </Box>
      );
    };  

    render() {
        if (this.props.editor.isAutomationUser()) {
            const object = this.props.editor.getSelection().getSelectedObject();          

            if (object) {
                

                return (
                        <Accordion 
                            cyid={"maticUserPanel"}
                            defaultExpanded
                            onChange={
                                (event: React.ChangeEvent<{}>, expanded: boolean) => {
                                    if (expanded) { setTimeout(() => scrollToElement('maticUserPanel'), 300)}
                                }
                            }
                            style={{ ...styles.root}}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon color="action" />}
                                style={{
                                    ...styles.actionPanel,
                                    ...styles.expanded,
                                    ...styles.content,
                                }}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item>
                                        <SvgIcon>
                                            <path fill="#0000008a" d={ICONS.AUTOMATION} />
                                        </SvgIcon>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="subtitle1">Automation</Typography>
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container style={{ justifyContent: 'flex-start' }} alignItems="center">
                                    {this.renderTextPlacheholderSection()}
                                    <Typography 
                                      gutterBottom 
                                      variant="body1" 
                                      style={styles.label}>
                                      Colors
                                    </Typography>                                    
                                    <Grid item xs={12}>
                                        {this.renderDropdowns()}
                                    </Grid>
                                    <Grid item xs={12}>
                                        {this.renderAddDropdownButton()}
                                    </Grid>
                                    {this.renderThumbnailSection()}
                                </Grid>
                            </AccordionDetails>                         
                        </Accordion>
                );
            }
        }
        return null;
    }
}

export default withClock(withTranslation("rightbarObj")(AutomationPanel));

